import { useCallback, useRef, useState } from 'react';

// Progress event types from server
interface ChatMessageEvent {
  type: 'chat_message';
  content: string;
  messageIndex: number;
}

interface CodeEditStartEvent {
  type: 'code_edit_start';
  editIndex: number;
  processingMessage: string;
  estimatedLines: number;
}

interface CodeEditProgressEvent {
  type: 'code_edit_progress';
  editIndex: number;
  percentage: number;
  currentLines: number;
  estimatedLines: number;
}

interface CodeEditCompleteEvent {
  type: 'code_edit_complete';
  editIndex: number;
  completeMessage: string;
}

// Legacy chunk format (backward compatibility)
interface LegacyStreamChunk {
  content: string;
  accumulated: string;
  chunkIndex: number;
}

// Union type for all possible chunk/event types
type StreamChunk = ChatMessageEvent | CodeEditStartEvent | CodeEditProgressEvent | CodeEditCompleteEvent | LegacyStreamChunk;

interface StreamComplete {
  success: boolean;
  code: string;
  shortSummary: string;
  explanation: string;
  suggestions: any[];
  chatFormat: string;
  codeEditSummary?: string;
  codeEditCount?: number;
  codeEditCompleteSummary?: string;
  conversationalOnly?: boolean;
  sessionId: string;
  usage: any;
  budget: any;
  metadata: any;
  hasErrors?: boolean; // Whether parsing or validation failed
}

interface StreamError {
  error: string;
  sessionId?: string;
}

interface StreamSession {
  sessionId: string;
}

interface UseAIStreamOptions {
  onChunk?: (data: StreamChunk) => void;
  onSession?: (data: StreamSession) => void;
  onComplete?: (data: StreamComplete) => void;
  onError?: (data: StreamError) => void;
}

interface UseAIStreamReturn {
  streamRequest: (url: string, body: any) => Promise<void>;
  isStreaming: boolean;
  abort: () => void;
  error: string | null;
}

/**
 * Custom React hook for Server-Sent Events (SSE) AI streaming
 * 
 * Usage:
 * const { streamRequest, isStreaming, abort, error } = useAIStream({
 *   onChunk: (data) => console.log('Chunk:', data),
 *   onComplete: (data) => console.log('Complete:', data),
 *   onError: (error) => console.error('Error:', error)
 * });
 */
export function useAIStream(options: UseAIStreamOptions = {}): UseAIStreamReturn {
  const {
    onChunk,
    onSession,
    onComplete,
    onError
  } = options;

  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Abort the current streaming request
   */
  const abort = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    setIsStreaming(false);
  }, []);

  /**
   * Start a streaming request
   */
  const streamRequest = useCallback(async (url: string, body: any) => {
    // Clean up any existing connection
    abort();

    setIsStreaming(true);
    setError(null);

    try {
      // Create abort controller for fetch
      abortControllerRef.current = new AbortController();

      // Get auth token (same key as apiService uses)
      const token = localStorage.getItem('beemi_access_token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Make POST request to initiate stream
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Check if response is SSE
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('text/event-stream')) {
        throw new Error('Expected SSE response but got: ' + contentType);
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      // Process stream chunks
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event:')) {
            const eventType = line.slice(7).trim();
            
            // Next line should be data
            continue;
          }
          
          if (line.startsWith('data:')) {
            const dataStr = line.slice(6).trim();
            
            try {
              const data = JSON.parse(dataStr);
              
              // Handle different event types based on the data structure
              // Check for chat message events
              if (data.type === 'chat_message') {
                if (onChunk) {
                  onChunk(data);
                }
              } else if (data.type === 'code_edit_start' || data.type === 'code_edit_progress' || data.type === 'code_edit_complete' || data.type === 'code_edit_applied') {
                // New server-parsed progress event
                if (onChunk) {
                  onChunk(data);
                }
              } else if (data.type === 'processing_update') {
                // Processing update event (e.g., "Finalizing changes", "Fixing response format issues")
                if (onChunk) {
                  onChunk(data);
                  console.log('  → onChunk called successfully');
                }
              } else if (data.chunkIndex !== undefined) {
                // Legacy chunk event (for backward compatibility)
                if (onChunk) {
                  onChunk(data);
                }
              } else if (data.sessionId && data.success === undefined && !data.error && !data.code) {
                // This is a session event (only sessionId, no success/error/code)
                if (onSession) {
                  onSession(data);
                }
              } else if (data.success !== undefined) {
                // This is a complete event (success can be true or false)
                if (onComplete) {
                  onComplete(data);
                }
                setIsStreaming(false);
              } else if (data.error) {
                // This is an error event
                setError(data.error);
                if (onError) {
                  onError(data);
                }
                setIsStreaming(false);
              }
            } catch (parseError) {
              console.warn('⚠️ Failed to parse SSE data:', dataStr, parseError);
            }
          }
          
          // Handle keep-alive pings (: prefix)
          if (line.startsWith(':')) {
            // Ignore keep-alive pings
            continue;
          }
        }
      }

    } catch (err: any) {
      // Handle abort (not an error)
      if (err.name === 'AbortError') {
        console.log('Stream aborted by user');
        return;
      }

      const errorMessage = err.message || 'Failed to establish streaming connection';
      setError(errorMessage);
      
      if (onError) {
        onError({ error: errorMessage });
      }
      
      setIsStreaming(false);
    }
  }, [abort, onChunk, onSession, onComplete, onError]);

  // Cleanup on unmount
  // useEffect(() => {
  //   return () => {
  //     abort();
  //   };
  // }, [abort]);

  return {
    streamRequest,
    isStreaming,
    abort,
    error
  };
}

